/**
 * Phase 2 Wave 1 (Task C) — InstancedMesh batching for plain INSERTs.
 *
 * # Problem
 *
 * Drawings frequently contain many INSERTs of the same block at the
 * same scale + rotation but different positions (e.g. a grid of
 * identical fixtures, repeated furniture symbols, schedule markers).
 * The default flatten path emits one set of `RenderableEntity`s per
 * INSERT — for N identical INSERTs we ship N × children worth of
 * vertices through the line bucket / scene mesh pipeline.
 *
 * # Design
 *
 * Group identical INSERTs by `(blockName, scaleX, scaleY, scaleZ,
 * rotation-bucketed-to-1°)` and batch the group of size N >= 4 into
 * one `THREE.InstancedMesh` whose geometry is the shared block flatten
 * result and whose instance matrices encode the per-INSERT
 * translation.
 *
 * The threshold N = 4 reflects the break-even point measured in
 * cycle-2 profiling: for triples (N=3) the per-instance matrix update
 * + InstancedMesh's batched draw call cost roughly the same as 3 plain
 * meshes, and at N=4 the instanced path wins by ~15-20% wall time.
 * Smaller groups pass through the existing flatten pipeline unchanged
 * so we never lose performance.
 *
 * # Rotation bucketing — why 1° granularity?
 *
 * AutoCAD's typical drawings encode INSERT rotation as a finite set of
 * values (0, 90, 180, 270, 45, 30...). True floating-point exactness
 * would prevent grouping legitimate matches that differ by 1e-15 noise
 * from a roundtrip. 1° (`Math.PI / 180` rad ≈ 0.017) is well below
 * the visible threshold at any practical zoom but loose enough to
 * absorb arithmetic drift.
 *
 * # Scale bucketing — why exact match?
 *
 * Scale axes (X/Y/Z) carry semantic meaning — `(1, 1, 1)` and
 * `(1, -1, 1)` are mirror INSERTs and look different. We compare with
 * a tight 1e-9 tolerance to absorb numeric noise without collapsing
 * intentional variants.
 *
 * # AutoCAD parity
 *
 * Rendered output is bit-identical to the per-INSERT flatten path —
 * every cell shows the same block content at the same position. We
 * only batch the draw call.
 *
 * # Use site
 *
 * `collectInstancedBlockInserts(entities)` is the pre-pass: returns
 * the groups of size >=4 PLUS the unbatched INSERTs (singletons, pairs,
 * triples) so the caller can route each list to its own materializer.
 * `materializeInstancedBlockInsertMesh(group, ...)` produces the
 * actual `THREE.InstancedMesh`.
 */

import * as THREE from "three";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  safeRecordLookup,
  type BlockDefinition,
  type DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { flattenBlockReference } from "./typed/blockFromEntity";
import { applyOffset } from "../coordinateOrigin";
import type { RenderableEntity } from "../types";
import { geometryToLineVertices } from "../batchedLayer";
import { decodeAnnotativeContextsJson } from "../../scene/cannoscale/useCannoscaleResolver";

import type { AdapterContext } from "./adapterContext";

/** Threshold below which a group falls back to the plain per-INSERT
 *  flatten path. See module JSDoc for rationale. */
export const INSTANCED_INSERT_MIN_COUNT = 4;

/** Rotation bucket granularity in radians (= 1°). */
const ROTATION_BUCKET_RAD = Math.PI / 180;

/** A grouped set of identical INSERTs that share a block + scale +
 *  rotation tuple. The renderer materializes each group as ONE
 *  `THREE.InstancedMesh` whose instance matrices encode the per-INSERT
 *  translation. */
export interface InstancedBlockInsertGroup {
  blockName: string;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  /** Rotation already bucketed (= rotationDeg rounded to integer). */
  rotationDeg: number;
  /** The original INSERT EntityPreviews — the materializer reads each
   *  Insertion X/Y/Z to build the per-instance translation matrix. */
  inserts: EntityPreview[];
}

/** Output of the pre-pass. `groups` are batchable (size >= threshold);
 *  `passthrough` is everything else (singletons + pairs + triples). */
export interface CollectInstancedBlockInsertsResult {
  groups: InstancedBlockInsertGroup[];
  passthrough: EntityPreview[];
}

/**
 * Read the INSERT's scale/rotation/blockName from its property bag.
 * Mirrors `flattenBlockInsertEntity`'s parsing so groups and the
 * flatten cache share the same identity.
 */
function readInsertProps(entity: EntityPreview): {
  blockName: string;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  rotationDeg: number;
} {
  const p = entity.properties;
  const blockName =
    p["Name"] ?? p["Block name"] ?? p["BlockName"] ?? "";
  const sx = Number(p["Scale X"]);
  const sy = Number(p["Scale Y"]);
  const sz = Number(p["Scale Z"] ?? "1");
  const rot = Number(p["Rotation"] ?? "0");
  return {
    blockName,
    scaleX: Number.isFinite(sx) && Math.abs(sx) > 1e-9 ? sx : 1,
    scaleY: Number.isFinite(sy) && Math.abs(sy) > 1e-9 ? sy : 1,
    scaleZ: Number.isFinite(sz) && Math.abs(sz) > 1e-9 ? sz : 1,
    rotationDeg: Number.isFinite(rot) ? rot : 0,
  };
}

/** Quantize a scale axis so 1.0 and 0.9999999999 collapse. 9 decimals
 *  is well within Float32 precision and absorbs all common DXF round-
 *  trip drift. */
function quantizeScale(s: number): number {
  return Math.round(s * 1e9) / 1e9;
}

/** Round rotation (degrees) to the nearest 1° bucket. */
function quantizeRotationDeg(rotDeg: number): number {
  const rad = (rotDeg * Math.PI) / 180;
  const bucketedRad = Math.round(rad / ROTATION_BUCKET_RAD) * ROTATION_BUCKET_RAD;
  return (bucketedRad * 180) / Math.PI;
}

/**
 * Walk every INSERT-typed `EntityPreview` and bucket them into groups
 * by `(blockName, scaleX, scaleY, scaleZ, rotationDeg-bucketed)`.
 *
 * Returns:
 *   - `groups`: every bucket whose size meets `INSTANCED_INSERT_MIN_COUNT`.
 *   - `passthrough`: every INSERT that did NOT meet the threshold (so
 *     the caller can feed it through the per-INSERT flatten path) plus
 *     every non-INSERT entity unchanged.
 *
 * Non-INSERT entities pass through unmodified — the function is safe
 * to call on the full entity feed.
 *
 * # F3 (codex P2) — DRAWORDER preservation
 *
 * The `passthrough` list MUST be returned in the **original feed
 * order**. The caller (`SceneBulkLoad`) feeds passthrough into the per-
 * entity adapter loop and uses the loop index as `zOrderIndex`; the
 * scene's painter then sorts by `(zOrder, id)`. The previous
 * implementation seeded `passthrough` with every non-Block entity in
 * order, *then appended* the below-threshold INSERTs at the end —
 * which mangled DRAWORDER on mixed scenes (e.g. a hatch interleaved
 * with grid-aligned fixtures would paint on top of those fixtures
 * regardless of its original position in the feed). The two-pass walk
 * below — first collect groups, then walk the feed again — preserves
 * the original index for both kinds.
 */
export function collectInstancedBlockInserts(
  entities: readonly EntityPreview[],
  // S10d (@codex round-20, blockInsertInstanced:262 — "Skip scale-limited INSERTs
  // from instancing") — optional typed resolver so `isAnnotativeInsert` can read
  // the OBJECTSCALE allow-list (`BlockInsertEntity.annotationScales`), which is
  // NOT projected into the preview bag. Absent (unit callers that only feed
  // previews) → falls back to the `__entity_annotative_contexts` channel alone.
  resolveTyped?: (id: string) => DrawingEntity | null,
  // S10e — block definition table so `isDynamicBlockInsert` can check
  // whether the referenced block carries `dynamicMetadata`. Optional for
  // the same reason `resolveTyped` is (unit callers that only feed bare
  // previews have no block-definition data to check).
  blocks?: Record<string, BlockDefinition>,
): CollectInstancedBlockInsertsResult {
  // Pass 1: bucket every Block by the group key, recording each
  // insert's original feed index so we can detect group membership
  // during pass 2 without re-running `readInsertProps`.
  const byTuple = new Map<string, InstancedBlockInsertGroup>();
  // Maps the original feed index → group key (only for INSERTs that
  // joined a tentative group; non-Block entities are absent).
  const indexToGroupKey = new Map<number, string>();

  for (let i = 0; i < entities.length; i++) {
    const e = entities[i]!;
    if (e.type !== "Block") continue;
    // S10d — an annotative INSERT must resolve its per-scale representation
    // (visibility + per-scale insertion / scale / rotation) through the typed
    // `blockFromEntity` flatten. The flat InstancedMesh path shares ONE
    // (scale, rotation) tuple + one geometry across every cell and cannot
    // express per-scale visibility or a per-instance annotative transform, so
    // grouping an annotative INSERT here would silently render it at its master
    // scale and never hide it. Skip grouping → it falls to `passthrough`
    // (pass 2 emits it because its index gets no group key) → `blockFromEntity`.
    if (isAnnotativeInsert(e, resolveTyped)) continue;
    // S10e — a dynamic-block INSERT must resolve its OWN per-instance
    // `customProperties` / `visibilityState` through `evaluateBlock` (via
    // `blockFromEntity`). The InstancedMesh path shares ONE flattened
    // geometry (evaluated at the block's DEFAULT state, no per-instance
    // input) across every grouped INSERT, so two dynamic-block instances at
    // the same scale/rotation but different state would silently render
    // IDENTICAL (wrong-for-at-least-one) geometry. See `isDynamicBlockInsert`
    // doc comment.
    if (isDynamicBlockInsert(e, resolveTyped, blocks)) continue;
    const { blockName, scaleX, scaleY, scaleZ, rotationDeg } =
      readInsertProps(e);
    if (!blockName) continue;
    const sx = quantizeScale(scaleX);
    const sy = quantizeScale(scaleY);
    const sz = quantizeScale(scaleZ);
    const rd = quantizeRotationDeg(rotationDeg);
    const key = `${blockName}|${sx}|${sy}|${sz}|${rd.toFixed(6)}`;
    let group = byTuple.get(key);
    if (!group) {
      group = {
        blockName,
        scaleX: sx,
        scaleY: sy,
        scaleZ: sz,
        rotationDeg: rd,
        inserts: [],
      };
      byTuple.set(key, group);
    }
    group.inserts.push(e);
    indexToGroupKey.set(i, key);
  }

  // Promote buckets that meet the threshold to first-class groups; the
  // rest collapse to `passthroughGroupKeys` so pass 2 knows to route
  // their members back into `passthrough`.
  const groups: InstancedBlockInsertGroup[] = [];
  const passthroughGroupKeys = new Set<string>();
  for (const [key, group] of byTuple) {
    if (group.inserts.length >= INSTANCED_INSERT_MIN_COUNT) {
      groups.push(group);
    } else {
      passthroughGroupKeys.add(key);
    }
  }

  // Pass 2: walk the feed again in order, emitting each non-grouped
  // entity into `passthrough` at its original position. The result
  // preserves DRAWORDER for everything that didn't make it into a
  // batched group.
  const passthrough: EntityPreview[] = [];
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i]!;
    const groupKey = indexToGroupKey.get(i);
    if (groupKey === undefined) {
      // Non-Block / missing-blockName Block — always passthrough.
      passthrough.push(e);
      continue;
    }
    if (passthroughGroupKeys.has(groupKey)) {
      passthrough.push(e);
    }
    // else: belongs to a batched group, do not emit to passthrough.
  }
  return { groups, passthrough };
}

/**
 * S10d — is this INSERT scale-limited (annotative), so it must stay OFF the flat
 * InstancedMesh path and resolve per-scale visibility / transform through
 * `blockFromEntity`?
 *
 * TWO independent signals mark it (either → true):
 *   1. A non-empty `__entity_annotative_contexts` preview channel (the SAME JSON
 *      payload the resolver reads) — the INSERT carries per-scale representations.
 *   2. (@codex round-20, blockInsertInstanced:262) A non-empty OBJECTSCALE
 *      allow-list on the TYPED `BlockInsertEntity.annotationScales`. That list is
 *      mutated INDEPENDENTLY of `annotativeContexts` (`objectscale-add` can admit
 *      a scale with ZERO contexts) and is NOT projected into the preview bag, so
 *      the preview-only check (1) misses it. Such an INSERT can be HIDDEN by
 *      `annotationScaleAllowListHides` at a non-member scale (ANNOALLVISIBLE=false)
 *      — a per-scale visibility the shared-geometry instanced path cannot express.
 *      Consulting the typed resolver keeps it on the flatten path where the gate
 *      runs. Absent resolver → check (1) alone (golden parity for preview-only
 *      callers).
 */
function isAnnotativeInsert(
  entity: EntityPreview,
  resolveTyped?: (id: string) => DrawingEntity | null,
): boolean {
  if (
    decodeAnnotativeContextsJson(entity.properties.__entity_annotative_contexts)
      .length > 0
  )
    return true;
  const typed = resolveTyped?.(entity.id);
  return (
    typed?.type === "Block" && (typed.annotationScales?.length ?? 0) > 0
  );
}

/**
 * S10e — is this INSERT a dynamic-block instance, so it must stay OFF the
 * flat InstancedMesh path (same rationale as `isAnnotativeInsert` above,
 * different trigger)?
 *
 * The InstancedMesh path flattens the block's shared geometry EXACTLY ONCE
 * per group (`materializeInstancedBlockInsertMesh` → `flattenBlockReference`,
 * which evaluates at the block's AUTHORED DEFAULT state — no per-reference
 * `customProperties` / `visibilityState` in the typed model, module doc on
 * `blockFromEntity.ts` "Dynamic-block evaluation") and shares that ONE result
 * across every grouped INSERT's instance matrix. `blockFromEntity`'s
 * per-INSTANCE `evaluateBlock` resolution means two INSERTs of the SAME
 * dynamic block at the SAME scale/rotation but DIFFERENT `customProperties`
 * / `visibilityState` must render DIFFERENT geometry — the instanced fast
 * path has no per-instance evaluate step, so it cannot express that. ANY
 * dynamic-block INSERT (its block definition carries `dynamicMetadata`,
 * regardless of whether THIS particular instance diverges from the default)
 * is therefore excluded here and falls to `passthrough` → the per-INSERT
 * `blockFromEntity` flatten, which resolves each instance's own state
 * correctly. Absent `resolveTyped` / `blocks` → cannot detect, defaults to
 * `false` (matches `isAnnotativeInsert`'s "absent resolver → no signal"
 * precedent; unit callers that only feed bare previews have no block-
 * definition data to check against anyway).
 */
function isDynamicBlockInsert(
  entity: EntityPreview,
  resolveTyped: ((id: string) => DrawingEntity | null) | undefined,
  blocks: Record<string, BlockDefinition> | undefined,
): boolean {
  if (!resolveTyped || !blocks) return false;
  const typed = resolveTyped(entity.id);
  if (typed?.type !== "Block") return false;
  // KNOWN GAP (local Codex review, P1, pre-PR; shared with
  // `blockFromEntity.ts`'s `resolveEvaluatedChildren` — see that
  // function's doc comment "anonymous *U variant gap" for the full
  // investigation, evidence, and why a real fix needs a NEW persistent-
  // model field, not a quick patch here): an INSERT referencing an
  // anonymous `*U<N>` AutoCAD-baked variant of a dynamic master has
  // `blocks[typed.blockName].dynamicMetadata === undefined` (the master's
  // eval graph lives on a DIFFERENT, unresolved `BlockDefinition`), so
  // such an INSERT is NOT excluded here and remains eligible for
  // InstancedMesh batching — consistent with (not worse than) the fact
  // that `blockFromEntity` itself would ALSO render it statically today.
  // Codex PR #784 round 3 P2 — own-property-safe block-table read: a bare index
  // on an INSERT naming `"constructor"` returns the inherited prototype member,
  // whose `.dynamicMetadata` is `undefined` — the same verdict by accident here,
  // but the read itself is the hazard the one-truth helper removes everywhere.
  return (
    safeRecordLookup(blocks, typed.blockName)?.dynamicMetadata !== undefined
  );
}

/**
 * Extract a group INSERT's translation in DXF (pre-offset) space.
 * Exposed for the renderer's `materializeInstancedBlockInsertMesh`
 * so the per-instance matrices can be written from the raw entity
 * preview without dragging the full adapter context here.
 */
export function readInsertTranslation(
  entity: EntityPreview,
): { x: number; y: number; z: number } {
  const p = entity.properties;
  const ix = Number(p["Insertion X"]);
  const iy = Number(p["Insertion Y"]);
  const iz = Number(p["Insertion Z"] ?? "0");
  return {
    x: Number.isFinite(ix) ? ix : 0,
    y: Number.isFinite(iy) ? iy : 0,
    z: Number.isFinite(iz) ? iz : 0,
  };
}

/* ── Phase 2 Wave 2 Task 4 — InstancedMesh materializer ───────────── */

/**
 * Output of the materializer: a `THREE.InstancedMesh` per resolved
 * block child geometry, plus diagnostic metadata so the scene can
 * route hit-tests / selection back to the original INSERT entity ids.
 *
 * `inserts` carries the same ids as the group input, in instance-matrix
 * order — selection code uses `instanceId → inserts[instanceId].id`.
 */
export interface InstancedBlockInsertMesh {
  mesh: THREE.InstancedMesh;
  /** The INSERT entity ids, in instance-matrix order. */
  insertIds: ReadonlyArray<string>;
  /** Block name from the group (for diagnostic / userData attribution). */
  blockName: string;
  /**
   * F4 (codex P2) — per-instance AABB in offset-world space, parallel
   * to `insertIds`. Lets the scene register a synthetic
   * `RenderableEntity` per grouped INSERT in the spatial index so
   * pick / hover / window-select / fit-to-extents see it. Without this
   * the InstancedMesh is rendered but the underlying entity ids are
   * invisible to every entity-id-keyed registry.
   */
  instanceBounds: ReadonlyArray<{
    id: string;
    bounds: { minX: number; minY: number; maxX: number; maxY: number };
  }>;
}

/**
 * Phase 2 Wave 2 Task 4 — materialise an `InstancedBlockInsertGroup`
 * into one or more `THREE.InstancedMesh` objects.
 *
 * # Pattern
 *
 * Mirrors `buildArrayInstancedMeshes` in `sceneMeshFactories.ts`:
 *
 *   1. Flatten the block ONCE at scale + rotation centred on origin
 *      via `flattenBlockInsert` with insertion = (0,0,0).
 *   2. Build a per-child shared `BufferGeometry` from each flattened
 *      child entity's line vertices.
 *   3. Allocate one `THREE.InstancedMesh` per child geometry, with
 *      one instance matrix per INSERT in the group (translation only —
 *      scale + rotation are baked into the shared geometry).
 *
 * Returns one entry per resolved child geometry. An empty array means
 * the block did not resolve (unknown name / cycle / depth limit) — the
 * caller should fall back to the per-INSERT flatten path for the
 * group's inserts.
 *
 * # Why translation-only matrices?
 *
 * The group's `scaleX/Y/Z` and `rotationDeg` are constants across
 * every INSERT in the group (that's what makes it a group). Baking
 * those into the shared geometry means every per-instance matrix is
 * a pure translation — `m.makeTranslation(tx, ty, tz)` — which the
 * GPU vertex shader applies in one MAD. If we left scale + rotation
 * in the matrices we'd transfer 16 floats per instance × N instances;
 * with translation-only we transfer 3 floats × N (still 16 because
 * Matrix4 is the THREE.js API, but the GPU branch is trivial).
 *
 * # Origin offset
 *
 * `readInsertTranslation` returns DXF-world coordinates; we route them
 * through `applyOffset` to keep Float32 precision on geodetic surveys
 * — same contract as `flattenBlockInsertEntity` itself.
 *
 * # AutoCAD parity
 *
 * Rendered output is bit-identical to N per-INSERT plain meshes (each
 * with the same flattened block content). Only the draw call is
 * batched.
 */
export function materializeInstancedBlockInsertMesh(
  group: InstancedBlockInsertGroup,
  blocks: Record<string, BlockDefinition>,
  ctx: AdapterContext,
): InstancedBlockInsertMesh[] {
  const { blockName, scaleX, scaleY, scaleZ, rotationDeg, inserts } = group;
  if (inserts.length === 0) return [];

  // Step 1: flatten the block centred on origin. We pass an `insertion`
  // of (0,0,0) so the resulting renderables are in "shared local"
  // coords; the per-instance translation matrix offsets them per-cell.
  //
  // DEFUSE S11 — flattens through the TYPED `flattenBlockReference` (the
  // legacy `flattenBlockInsert` is deleted). NOTE: the legacy call passed
  // `(rotationDeg * Math.PI) / 180` into a DEGREES parameter — a latent
  // unit bug that never manifested because this shared-geometry output is
  // only consumed when NO flattened child is line-class (see the
  // `anyLineContent` fallback below), which no real block satisfies. The
  // typed call passes the group's `rotationDeg` (degrees) correctly.
  const sharedChildren: RenderableEntity[] = flattenBlockReference({
    blockName,
    insertion: { x: 0, y: 0, z: 0 },
    scale: { x: scaleX, y: scaleY, z: scaleZ },
    rotationDeg,
    spaceId: inserts[0]!.spaceId,
    parentEntityId: inserts[0]!.id,
    blocks,
    ctx,
  });

  if (sharedChildren.length === 0) return [];

  // F2/F5 (codex P1, post-cycle-1) — every primitive kind emitted by
  // `flattenBlockInsert` resolves to vertex *pairs* (gl.LINES topology
  // that `geometryToLineVertices` produces). Wrapping such positions in
  // `THREE.InstancedMesh` is incorrect: `InstancedMesh` ships triangle
  // topology, so half the segments would be reinterpreted as triangle
  // vertices and the rest dropped, producing visibly wrong geometry.
  //
  // Cycle-1 fix gated on `allLineContent` (every child must be line),
  // but mixed-content blocks (e.g. line + text inside a single block —
  // a fixture symbol with an annotation) still entered the InstancedMesh
  // path because at least one child wasn't line. Codex P1 #2 flagged
  // that as a fidelity regression. The conservative fix is to fall back
  // for *any* group whose flattened children contain ANY line-content
  // child — the InstancedMesh path is reserved exclusively for groups
  // whose flattened children are ALL triangle-topology kinds
  // (hatch/wipeout/array-rect). `flattenBlockInsert` does not currently
  // emit those kinds, so in practice every real group hits the fallback.
  //
  // The instanced-mesh code path is preserved as scaffolding for the
  // follow-up that adds hatch/wipeout/array-rect support to flatten.
  const anyLineContent = sharedChildren.some((c) => isLineGeometry(c.geometry.kind));
  if (anyLineContent) return [];

  // Step 2: build a single triangle `BufferGeometry` from the flattened
  // children. Only reached for triangle-content blocks (see F2 comment
  // above).
  const positions: number[] = [];
  for (const child of sharedChildren) {
    const verts = geometryToLineVertices(child.geometry);
    for (const v of verts) positions.push(v);
  }

  if (positions.length === 0) return [];

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(positions), 3),
  );
  // F4 — compute the shared-geometry AABB once. Each per-instance
  // bounds is this box translated by the instance's offset.
  geometry.computeBoundingBox();
  const sharedBox = geometry.boundingBox;
  const sharedMinX = sharedBox?.min.x ?? 0;
  const sharedMinY = sharedBox?.min.y ?? 0;
  const sharedMaxX = sharedBox?.max.x ?? 0;
  const sharedMaxY = sharedBox?.max.y ?? 0;

  // Resolve a base color from the first child (mirrors the array-rect
  // factory; full per-child color support would require splitting one
  // InstancedMesh per (color, linetype) tuple).
  const colorHex = sharedChildren[0]?.colorHex ?? "#ffffff";
  const material = new THREE.MeshBasicMaterial({
    color: new THREE.Color(colorHex).getHex(),
  });

  // Step 3: allocate the InstancedMesh and write one translation
  // matrix per INSERT.
  const mesh = new THREE.InstancedMesh(geometry, material, inserts.length);
  // F4 (codex P1 #4) — per-instance layer + original instance matrix
  // stored on userData so the layer-visibility updater can hide
  // individual instances when their layer is hidden. The updater
  // overwrites a hidden instance's matrix with a zero matrix
  // (degenerate geometry → invisible) and restores from
  // `instanceLayers`/`instanceMatricesOriginal` when the layer
  // becomes visible again.
  const instanceLayers: string[] = inserts.map(
    (i) => i.properties["Layer"] ?? "0",
  );
  const instanceMatricesOriginal: THREE.Matrix4[] = [];
  mesh.userData = {
    kind: "block-insert-instanced",
    blockName,
    insertIds: inserts.map((i) => i.id),
    instanceLayers,
    instanceMatricesOriginal,
  };
  const m = new THREE.Matrix4();
  const instanceBounds: { id: string; bounds: { minX: number; minY: number; maxX: number; maxY: number } }[] = [];
  for (let i = 0; i < inserts.length; i++) {
    const insert = inserts[i]!;
    const t = readInsertTranslation(insert);
    const offset = applyOffset({ x: t.x, y: t.y, z: t.z });
    m.makeTranslation(offset.x, offset.y, offset.z);
    mesh.setMatrixAt(i, m);
    // Save a clone — `m` is reused across iterations.
    instanceMatricesOriginal.push(m.clone());
    instanceBounds.push({
      id: insert.id,
      bounds: {
        minX: sharedMinX + offset.x,
        minY: sharedMinY + offset.y,
        maxX: sharedMaxX + offset.x,
        maxY: sharedMaxY + offset.y,
      },
    });
  }
  mesh.instanceMatrix.needsUpdate = true;
  // Frustum culling on InstancedMesh uses the geometry's bounding box
  // which would be `(0,0,0)` for our centred shared geometry — culling
  // would incorrectly drop instances at world coords. Disable so every
  // instance is drawn unconditionally.
  mesh.frustumCulled = false;

  return [
    {
      mesh,
      insertIds: inserts.map((i) => i.id),
      blockName,
      instanceBounds,
    },
  ];
}

/**
 * F2/F5 (codex P1, post-cycle-1) — sniff whether a `SceneGeometry.kind`
 * resolves through `geometryToLineVertices` to gl.LINES vertex pairs
 * (rather than to triangle topology). Used by
 * `materializeInstancedBlockInsertMesh` to decide whether the instanced
 * path is correct for the group's content or whether to fall back to
 * per-INSERT flatten via passthrough.
 *
 * Kept in sync with `geometryToLineVertices` in `../batchedLayer.ts`:
 * every kind that helper handles emits 2-vertex segments. `hatch` /
 * `wipeout` / `array-rect` are NOT line content (they emit triangle
 * topology); everything else this list enumerates IS line content.
 *
 * `text` / `mtext` are line-class FOR THE PURPOSES OF THIS PREDICATE
 * because the InstancedMesh path cannot represent them either (they
 * materialise as separate `THREE.Mesh` text objects with SDF atlas
 * textures, not vertex streams). A flattened block containing TEXT
 * or MTEXT children must therefore fall back to the per-INSERT
 * passthrough so each text child can be emitted as its own renderable.
 */
function isLineGeometry(kind: string): boolean {
  // The first group is the actual line-class kinds emitted by
  // `geometryToLineVertices`. The second group (`text` / `mtext` /
  // `dimension` / `multileader`) materialise as their own THREE objects
  // — not vertex-stream triangle content — so they also cannot live
  // inside the shared InstancedMesh geometry. Treat them as
  // "non-triangle" so a block containing any of them falls back to
  // per-INSERT passthrough.
  switch (kind) {
    case "line":
    case "polyline":
    case "circle":
    case "arc":
    case "ellipse":
    case "spline":
    case "point":
    case "text":
    case "mtext":
    case "dimension":
    case "multileader":
      return true;
    default:
      return false;
  }
}
